import { ref, computed, onMounted, onBeforeUnmount, watch, type MaybeRefOrGetter, toValue } from 'vue';
import { ibWS } from '@ibgroup-tws-webapps/ib.utils';
import { useUserState } from '@/stores/useUserState';
import type { EventContractRatingResponse } from '@/types/mdfarm.types';
import type { StrikeGroup } from '@/types/contracts.types';

export interface EventContractRatingStreamOptions {
  isLive?: MaybeRefOrGetter<boolean>;
}

/**
 * Subscribes to the event-contract rating service for a set of `conid@exch` pairs
 * and exposes which venue is the "best" per the service's rating calculation.
 *
 * The crown ("best price") indicator on the Product Details page is driven entirely
 * by the `best` flag returned here — the client no longer computes best venue itself.
 * Always requests `size = 1` (products-page guidance) and is authenticated-only, since
 * the rating request requires an `account_id`.
 *
 * See BMICROSERV-13478 / FORECASTUI-2234.
 */
export function useEventContractRatingStream(
  conidExPairs: MaybeRefOrGetter<string[]>,
  options: EventContractRatingStreamOptions = {},
) {
  const bestByConidEx = ref<Map<string, boolean>>(new Map());
  const userState = useUserState();

  // Rating requires an account_id, so only stream when authenticated with an account.
  const shouldStream = computed(() => !userState.usePublicApi && !!userState.account);

  let requestId: string | null = null;
  let subscription: { unsubscribe(): void } | null = null;

  async function subscribe() {
    const pairs = toValue(conidExPairs);
    if (!pairs || pairs.length === 0) return;

    const webAppId = await ibWS.getWebAppId();
    requestId = `ec-rating-${pairs.join(',')}-${webAppId}`;

    ibWS.sendMessage({
      message: {
        command: 'SEND',
        headers: {
          destination: '/event-contract/rating',
          'x-target': 'order',
          id: requestId,
        },
        body: {
          account_id: userState.account,
          size: 1,
          contracts: pairs.map((conid_ex) => ({ conid_ex })),
        },
      },
    });

    const observable = ibWS.receive(requestId);
    if (observable) {
      subscription = observable.subscribe({
        /* eslint-disable @typescript-eslint/no-explicit-any */
        next: (msg: any) => {
          const body = (msg?.payload ?? msg?.body ?? msg) as Partial<EventContractRatingResponse>;
          if (!body || !Array.isArray(body.results)) return;
          const next = new Map(bestByConidEx.value);
          for (const result of body.results) {
            if (!result?.conid_ex) continue;
            next.set(result.conid_ex, result.best === true);
          }
          bestByConidEx.value = next;
        },
      });
    }
  }

  function unsubscribe() {
    if (requestId) {
      ibWS.sendMessage({
        message: {
          command: 'SEND',
          headers: {
            destination: '/event-contract/rating/unsubscribe',
            'x-target': 'order',
            id: requestId,
          },
        },
      });
    }
    subscription?.unsubscribe();
    subscription = null;
    requestId = null;
    bestByConidEx.value = new Map();
  }

  /** True when the rating service flagged this contract's venue as best. */
  function isBest(conid: number | undefined, exchange: string): boolean {
    if (!conid) return false;
    return bestByConidEx.value.get(`${conid}@${exchange}`) === true;
  }

  onMounted(() => {
    if (shouldStream.value && (options.isLive === undefined || toValue(options.isLive))) {
      subscribe();
    }
  });

  onBeforeUnmount(() => {
    unsubscribe();
  });

  if (options.isLive !== undefined) {
    watch(
      () => toValue(options.isLive!),
      (live) => {
        if (!shouldStream.value) return;
        if (live && !subscription) {
          subscribe();
        } else if (!live && subscription) {
          unsubscribe();
        }
      },
    );
  }

  watch(shouldStream, (canStream) => {
    if (canStream && !subscription) {
      const pairs = toValue(conidExPairs);
      if (pairs.length > 0 && (options.isLive === undefined || toValue(options.isLive))) {
        subscribe();
      }
    } else if (!canStream && subscription) {
      unsubscribe();
    }
  });

  watch(
    () => toValue(conidExPairs),
    (newPairs, oldPairs) => {
      if (!shouldStream.value) return;
      if (JSON.stringify(newPairs) === JSON.stringify(oldPairs)) return;
      unsubscribe();
      if (newPairs.length > 0 && (options.isLive === undefined || toValue(options.isLive))) {
        subscribe();
      }
    },
  );

  return { bestByConidEx, isBest };
}

/**
 * Rating subscription manager for many strike groups at once (the contracts table).
 *
 * Each expandable group needs its YES venues rated against each other and its NO venues
 * rated against each other — "best" is scoped per request, so a single flat subscription
 * would be wrong. This manager maintains one subscription per (group, side), reconciling
 * them as the expanded set changes, and merges every result into one shared map keyed by
 * the globally-unique `conid@exch`.
 */
export function useGroupedEventContractRatingStream(groups: MaybeRefOrGetter<StrikeGroup[]>) {
  const bestByConidEx = ref<Map<string, boolean>>(new Map());
  const userState = useUserState();

  const shouldStream = computed(() => !userState.usePublicApi && !!userState.account);

  // One STOMP subscription per request key (group strike + side).
  interface RatingSub {
    requestId: string;
    pairs: string[];
    subscription: { unsubscribe(): void } | null;
  }
  const subs = new Map<string, RatingSub>();

  function applyResults(results: EventContractRatingResponse['results']) {
    if (!Array.isArray(results)) return;
    const next = new Map(bestByConidEx.value);
    for (const result of results) {
      if (!result?.conid_ex) continue;
      next.set(result.conid_ex, result.best === true);
    }
    bestByConidEx.value = next;
  }

  async function openSub(key: string, pairs: string[]) {
    const webAppId = await ibWS.getWebAppId();
    const requestId = `ec-rating-${key}-${webAppId}`;
    const sub: RatingSub = { requestId, pairs, subscription: null };
    subs.set(key, sub);

    ibWS.sendMessage({
      message: {
        command: 'SEND',
        headers: {
          destination: '/event-contract/rating',
          'x-target': 'order',
          id: requestId,
        },
        body: {
          account_id: userState.account,
          size: 1,
          contracts: pairs.map((conid_ex) => ({ conid_ex })),
        },
      },
    });

    const observable = ibWS.receive(requestId);
    if (observable) {
      sub.subscription = observable.subscribe({
        /* eslint-disable @typescript-eslint/no-explicit-any */
        next: (msg: any) => {
          const body = (msg?.payload ?? msg?.body ?? msg) as Partial<EventContractRatingResponse>;
          applyResults(body?.results ?? []);
        },
      });
    }
  }

  function closeSub(key: string) {
    const sub = subs.get(key);
    if (!sub) return;
    ibWS.sendMessage({
      message: {
        command: 'SEND',
        headers: {
          destination: '/event-contract/rating/unsubscribe',
          'x-target': 'order',
          id: sub.requestId,
        },
      },
    });
    sub.subscription?.unsubscribe();
    subs.delete(key);
  }

  function closeAll() {
    for (const key of [...subs.keys()]) closeSub(key);
    bestByConidEx.value = new Map();
  }

  /** Build the desired set of subscriptions: one per side of each expandable group. */
  function desiredSubs(): Map<string, string[]> {
    const result = new Map<string, string[]>();
    if (!shouldStream.value) return result;
    for (const group of toValue(groups)) {
      if (!group.expandable) continue;
      const yesPairs = group.contracts.map((c) => `${c.conid_yes}@${c.exchange}`);
      const noPairs = group.contracts.map((c) => `${c.conid_no}@${c.exchange}`);
      if (yesPairs.length > 1) result.set(`${group.strike_label}|yes`, yesPairs);
      if (noPairs.length > 1) result.set(`${group.strike_label}|no`, noPairs);
    }
    return result;
  }

  function reconcile() {
    const desired = desiredSubs();

    // Close subs that are gone or whose pairs changed.
    for (const [key, sub] of [...subs.entries()]) {
      const wanted = desired.get(key);
      if (!wanted || JSON.stringify(wanted) !== JSON.stringify(sub.pairs)) {
        closeSub(key);
      }
    }

    // Open subs that are newly wanted.
    for (const [key, pairs] of desired) {
      if (!subs.has(key)) openSub(key, pairs);
    }
  }

  watch(() => toValue(groups), reconcile, { deep: false });
  watch(shouldStream, reconcile);

  onMounted(reconcile);
  onBeforeUnmount(closeAll);

  function isBest(conid: number | undefined, exchange: string): boolean {
    if (!conid) return false;
    return bestByConidEx.value.get(`${conid}@${exchange}`) === true;
  }

  return { bestByConidEx, isBest };
}
